import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material/';
import { useSelector } from 'react-redux';
import { useErrorBoundary } from 'react-error-boundary';
import InfiniteScroll from 'react-infinite-scroll-component';
import ArticlesService from '../../../services/articles.service';
import Articles from '../../../views/articles/Articles';
import ContentPieceDTO from '../../../DTOs/ContentPieceDTO';
import useIsFirstRender from '../../../hooks/useIsFirstRender';
import Ghost from '../../../views/ghost/Ghost';
import { GhostTemplates } from '../../../enums/GhostTemplates';
import DateFilter from '../../filter/date-filter/DateFilter';
import PreferencesDropdown from '../../preferences-dropdown/PreferencesDropdown';
import FiltersChips from '../../../views/filters-chips/FiltersChips';
import classes from './all-articles.module.scss';
import Filters from '../../../views/filters/Filters';
import { Filters as FiltersInterface } from '../../../interfaces/Filters';
import { Content } from '../../../enums/Content';
import { RootState } from '../../../../store';

const ARTICLES_PER_LOAD = 7;
const END_MESSAGE = 'Yay! You have seen it all';

const AllArticles = () => {
  const { showBoundary } = useErrorBoundary();
  const [articles, setArticles] = useState<ContentPieceDTO[]>([]);
  const [areFiltersExpanded, setAreFiltersExpanded] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [areArticlesLoading, setAreArticlesLoading] = useState<boolean>(false);
  const filters = useSelector(
    (state: RootState) => state.filters.filters
  );
  const prevFilters = useRef<FiltersInterface>(filters);
  const firstRender = useIsFirstRender();

  const fetchArticles = useCallback((page: number) => {
    setAreArticlesLoading(true);
    ArticlesService.getFilteredWithPagination(
      page,
      ARTICLES_PER_LOAD,
      filters
    )
      .then((data) => {
        if (ARTICLES_PER_LOAD > data.length) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }

        setArticles(prevArticles => prevArticles.concat(data));
      })
      .catch((error: Error) => {
        setHasMore(false);
        showBoundary(error);
      })
      .finally(() => setAreArticlesLoading(false));
  }, [filters, showBoundary]);

  const areFiltersEmpty = useMemo(() => Object.values(filters).every((value) => value === null || value.length === 0), [filters]);

  useEffect(() => {
    const abortController = new AbortController();

    if (firstRender) {
      fetchArticles(1);

      return;
    }

    if (prevFilters.current !== filters) {
      fetchArticles(1);
      setArticles([]);
      setPageNumber(1);
      setHasMore(true);
    }

    prevFilters.current = filters;

    return () => abortController.abort();
  }, [filters, fetchArticles, firstRender]);

  const handleLoadMore = useCallback(() => {
    fetchArticles(pageNumber + 1);
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  }, [fetchArticles, pageNumber]);

  const expendFilterButtonHandler = () => {
    setAreFiltersExpanded(!areFiltersExpanded);
  };

  return (
    <>
      <Box className='d-flex flex-row justify-content-end justify-content-sm-between'>
        <Box className={classes['filters-box']}>
          {areFiltersExpanded ?
            <Box className={`d-flex flex-column border align-items-start p-2 ${classes['rounded-box']}`}>
              <Box>
                <Button className={`${classes['filters-button']}`} variant='text' onClick={expendFilterButtonHandler} >
                  Filters
                </Button>
              </Box>
              <Filters />
            </Box>
            :
            <Button className={`${classes['filters-button']} border`} variant='text' onClick={expendFilterButtonHandler} >
              Filters
            </Button>
          }
        </Box>
        <Box className='d-flex flex-row gap-3 mx-2 mx-sm-0 '>
          <PreferencesDropdown />
          <DateFilter content={Content.ARTICLES} />
        </Box>
      </Box>
      <Box className='mt-5 mb-2'>
        <FiltersChips filters={filters} />
      </Box>
      <Box className='pb-3'>
        <InfiniteScroll
          dataLength={articles.length}
          next={handleLoadMore}
          hasMore={hasMore}
          loader={
            <Ghost repeatValue={5} template={GhostTemplates.ARTICLE_CARD} />
          }
          endMessage={
            articles.length > 0 ?
              <Typography align='center' className='h3'>
                {END_MESSAGE}
              </Typography> :
              null
          }
        >
          <Articles articles={articles} areFiltersEmpty={areFiltersEmpty} loading={areArticlesLoading} />
        </InfiniteScroll>
      </Box >
    </>
  );
};

export default AllArticles;
